import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, RefreshCw, GitMerge, Trash2, Shuffle, Download, ChevronUp, ChevronDown, ArrowLeftRight, Undo2, Lock, Unlock, AlertTriangle, Shield, UserCheck, Edit3, X, Check, Trophy, ListOrdered } from "lucide-react";

const EVT_LABELS = { mens_singles: "Men's Singles", mens_doubles: "Men's Doubles", womens_singles: "Women's Singles", womens_doubles: "Women's Doubles", mixed_doubles: "Mixed Doubles" };
const SHORT = { mens_singles: "MS", mens_doubles: "MD", womens_singles: "WS", womens_doubles: "WD", mixed_doubles: "XD" };
const ALL_EVENTS = Object.keys(EVT_LABELS);
const ALL_AGES = ["U-10", "U-12", "U-14", "U-16", "U-19", "19+", "35+", "50+", "60+"];

function ageLbl(a) { return a === "19+" ? "Open (19+)" : a; }
function catLabel(c) { return `${EVT_LABELS[c.event_category] || c.event_category} · ${ageLbl(c.age_category)}`; }
function catShort(c) { return `${SHORT[c.event_category] || c.event_category} ${ageLbl(c.age_category)}`; }
function countLabel(c) { return c.team_count != null ? `${c.team_count} pairs · ${c.entry_count} players` : `${c.entry_count} ${c.entry_count === 1 ? "entry" : "entries"}`; }

// =========================================================================
// TAB 1 — Categories & Referees
// =========================================================================
function CategoriesTab({ tid, onCategoriesChange }) {
  const [cats, setCats] = useState([]);
  const [ev, setEv] = useState("mens_singles");
  const [ag, setAg] = useState("19+");
  const [mergeSrc, setMergeSrc] = useState("");
  const [mergeTgt, setMergeTgt] = useState("");
  const load = async () => {
    try { const r = await api.get(`/organizer/tournaments/${tid}/categories`); setCats(r.data); onCategoriesChange?.(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tid]);
  const sync = async () => {
    try { const r = await api.post(`/organizer/tournaments/${tid}/categories/sync`); toast.success(`Synced — ${r.data.synced} new (${r.data.total_pairs} total)`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resetHub = async () => {
    if (!window.confirm("FULL WIPE: this deletes all categories, pools, matches AND transient tournament_entries for this tournament.\n\nYour Quick Entries (real player registrations) are NEVER touched.\n\nAfter reset, click Auto-sync to rebuild from Quick Entries.\n\nContinue?")) return;
    if (!window.confirm("Are you absolutely sure? This cannot be undone.")) return;
    try {
      const r = await api.post(`/organizer/tournaments/${tid}/reset`);
      toast.success(`Reset done — ${r.data.categories_deleted} categories, ${r.data.pools_deleted} pools, ${r.data.matches_deleted} matches, ${r.data.entries_deleted} entries wiped. Hit Auto-sync now.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const add = async () => {
    try { await api.post(`/organizer/tournaments/${tid}/categories`, { event_category: ev, age_category: ag }); toast.success("Category added"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (cid, label, count) => {
    if (count > 0 && !window.confirm(`This category has ${count} entries. Delete anyway?`)) return;
    if (count === 0 && !window.confirm(`Delete "${label}"?`)) return;
    try { await api.delete(`/organizer/tournaments/${tid}/categories/${cid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const merge = async () => {
    if (!mergeSrc || !mergeTgt || mergeSrc === mergeTgt) { toast.error("Pick two different categories"); return; }
    const srcC = cats.find((c) => c.id === mergeSrc); const tgtC = cats.find((c) => c.id === mergeTgt);
    if (!window.confirm(`Merge "${catLabel(srcC)}" INTO "${catLabel(tgtC)}"?\nAll entries move to ${catLabel(tgtC)}. Source is deleted.\nYou can Unmerge later from the target category.`)) return;
    try {
      const r = await api.post(`/organizer/tournaments/${tid}/categories/merge`, { source_id: mergeSrc, target_id: mergeTgt });
      toast.success(`Merged ${r.data.merged} entries → ${r.data.target_name}`);
      setMergeSrc(""); setMergeTgt(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const unmerge = async (cid, label) => {
    if (!window.confirm(`Unmerge the last merge into "${label}"?\nOriginally-merged entries move back to their source category.\nEntries added AFTER the merge stay here.`)) return;
    try {
      const r = await api.post(`/organizer/tournaments/${tid}/categories/${cid}/unmerge`);
      toast.success(`Restored ${r.data.restored} entries → ${r.data.source_name}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="border border-jpa-border rounded-sm p-4 md:p-5 bg-white">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <h3 className="font-display text-2xl tracking-wider">Categories</h3>
            <p className="text-xs text-jpa-muted">Auto-synced from Quick Entries and approved tournament entries. Merge is reversible.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="cat-sync" onClick={sync} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold gap-1"><RefreshCw className="w-3 h-3" /> Auto-sync</Button>
            <Button data-testid="cat-reset" onClick={resetHub} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1 border-red-500 text-red-600 hover:bg-red-50"><AlertTriangle className="w-3 h-3" /> Reset hub</Button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3 border-t border-jpa-border pt-4 mb-4">
          <div>
            <Label className="text-xs">Event</Label>
            <Select value={ev} onValueChange={setEv}><SelectTrigger data-testid="cat-add-event" className="mt-1 rounded-sm h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{ALL_EVENTS.map((e) => <SelectItem key={e} value={e}>{EVT_LABELS[e]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Age</Label>
            <Select value={ag} onValueChange={setAg}><SelectTrigger data-testid="cat-add-age" className="mt-1 rounded-sm h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{ALL_AGES.map((a) => <SelectItem key={a} value={a}>{a === "19+" ? "Open (19+)" : a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button data-testid="cat-add" onClick={add} className="rounded-sm w-full h-10 bg-jpa-ink hover:bg-black text-white text-xs uppercase tracking-widest font-bold gap-1"><Plus className="w-3 h-3" /> Add category</Button>
          </div>
        </div>

        {cats.length === 0 ? (
          <div className="border border-dashed border-jpa-border p-6 text-center text-sm text-jpa-muted rounded-sm">No categories yet — hit Auto-sync or add manually.</div>
        ) : (
          <div className="space-y-2">
            {cats.map((c) => {
              const mergeCount = (c.merges || []).length;
              return (
                <div key={c.id} data-testid={`cat-row-${c.id}`} className="flex items-center justify-between gap-2 border border-jpa-border rounded-sm px-3 py-2 hover:border-jpa-primary transition-colors">
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">{ageLbl(c.age_category)}</div>
                    <div className="text-sm font-bold">{EVT_LABELS[c.event_category]}</div>
                    {mergeCount > 0 && <div className="text-[10px] text-amber-700 font-bold mt-0.5">↳ {mergeCount} merge{mergeCount > 1 ? "s" : ""} applied</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-jpa-surface text-jpa-primary px-2 py-1 rounded-sm">{countLabel(c)}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm ${c.source === "auto" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{c.source}</span>
                    {mergeCount > 0 && (
                      <button data-testid={`cat-unmerge-${c.id}`} onClick={() => unmerge(c.id, catLabel(c))} className="p-1.5 text-amber-700 hover:text-amber-900 border border-amber-300 rounded-sm inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest" aria-label="Unmerge"><Undo2 className="w-3 h-3" /> Unmerge</button>
                    )}
                    <button data-testid={`cat-delete-${c.id}`} onClick={() => del(c.id, catLabel(c), c.entry_count)} className="p-1.5 text-jpa-muted hover:text-red-600 rounded-sm" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {cats.length >= 2 && (
          <div className="mt-6 pt-4 border-t border-jpa-border">
            <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary mb-2 flex items-center gap-1"><GitMerge className="w-3 h-3" /> Merge categories</div>
            <div className="grid md:grid-cols-[1fr_auto_1fr_auto] gap-2 items-end">
              <div>
                <Label className="text-[10px]">Source (will be deleted)</Label>
                <Select value={mergeSrc} onValueChange={setMergeSrc}><SelectTrigger data-testid="cat-merge-src" className="mt-1 rounded-sm h-10"><SelectValue placeholder="Pick source" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{catLabel(c)} · {countLabel(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="hidden md:flex items-center justify-center pb-2"><ArrowLeftRight className="w-4 h-4 text-jpa-muted" /></div>
              <div>
                <Label className="text-[10px]">Target (keeps name, receives entries)</Label>
                <Select value={mergeTgt} onValueChange={setMergeTgt}><SelectTrigger data-testid="cat-merge-tgt" className="mt-1 rounded-sm h-10"><SelectValue placeholder="Pick target" /></SelectTrigger>
                  <SelectContent>{cats.filter((c) => c.id !== mergeSrc).map((c) => <SelectItem key={c.id} value={c.id}>{catLabel(c)} · {countLabel(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button data-testid="cat-merge-do" onClick={merge} disabled={!mergeSrc || !mergeTgt} className="rounded-sm h-10 bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold">Merge</Button>
            </div>
          </div>
        )}
      </div>

      <RefereesSection tid={tid} />
    </div>
  );
}

// ---- Referees section (Tab 1 lower half) ----
function RefereesSection({ tid }) {
  const [refs, setRefs] = useState([]);
  const [form, setForm] = useState({ name: "", username: "", password: "", phone: "" });
  const [assigning, setAssigning] = useState(false);
  const load = async () => {
    try { const r = await api.get(`/organizer/tournaments/${tid}/referees`); setRefs(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tid]);
  const add = async () => {
    if (!form.name.trim() || !form.username.trim() || !form.password.trim()) { toast.error("Name, username & password required"); return; }
    if (form.username.length < 3) { toast.error("Username too short (min 3)"); return; }
    if (form.password.length < 6) { toast.error("Password too short (min 6)"); return; }
    try {
      await api.post(`/organizer/tournaments/${tid}/referees`, form);
      toast.success(`Referee ${form.name} added — login username: ${form.username}`);
      setForm({ name: "", username: "", password: "", phone: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Remove referee ${r.name} (@${r.username})? Their login is deleted.`)) return;
    try { await api.delete(`/organizer/tournaments/${tid}/referees/${r.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const autoAssign = async () => {
    if (!refs.length) { toast.error("Add at least one referee first"); return; }
    setAssigning(true);
    try {
      const r = await api.post(`/organizer/tournaments/${tid}/matches/auto-assign-referees`);
      toast.success(`Assigned ${r.data.assigned} matches across ${r.data.referees} referee(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAssigning(false); }
  };

  return (
    <div className="border border-jpa-border rounded-sm p-4 md:p-5 bg-white">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h3 className="font-display text-2xl tracking-wider flex items-center gap-2"><Shield className="w-5 h-5 text-jpa-primary" /> Referees</h3>
          <p className="text-xs text-jpa-muted">Add referees for this tournament. They log in with username + password on /referee. Auto-assign uses round-robin across all unassigned matches.</p>
        </div>
        <Button data-testid="ref-auto-assign" onClick={autoAssign} disabled={assigning || !refs.length} className="rounded-sm bg-jpa-ink hover:bg-black text-white text-xs uppercase tracking-widest font-bold gap-1"><RefreshCw className="w-3 h-3" /> Auto-assign to matches</Button>
      </div>

      <div className="grid md:grid-cols-4 gap-2 border-t border-jpa-border pt-4 mb-4">
        <Input data-testid="ref-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-10" />
        <Input data-testid="ref-username" placeholder="Username (login)" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s+/g, "").toLowerCase() })} className="rounded-sm h-10 font-mono" />
        <Input data-testid="ref-password" type="text" placeholder="Password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm h-10" />
        <div className="flex gap-2">
          <Input data-testid="ref-phone" placeholder="Phone (opt)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm h-10 flex-1" />
          <Button data-testid="ref-add" onClick={add} className="rounded-sm h-10 bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold gap-1"><Plus className="w-3 h-3" /> Add</Button>
        </div>
      </div>

      {refs.length === 0 ? (
        <div className="border border-dashed border-jpa-border p-6 text-center text-sm text-jpa-muted rounded-sm">No referees yet — add one above.</div>
      ) : (
        <div className="space-y-2">
          {refs.map((r) => (
            <div key={r.id} data-testid={`ref-row-${r.id}`} className="flex items-center justify-between gap-2 border border-jpa-border rounded-sm px-3 py-2 hover:border-jpa-primary transition-colors">
              <div className="min-w-0">
                <div className="text-sm font-bold">{r.name}</div>
                <div className="text-[11px] text-jpa-muted font-mono">@{r.username}{r.phone ? ` · ${r.phone}` : ""}</div>
              </div>
              <button data-testid={`ref-delete-${r.id}`} onClick={() => del(r)} className="p-1.5 text-jpa-muted hover:text-red-600 rounded-sm" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================================================================
// TAB 2 — Players & Pools
// =========================================================================
function PoolsTab({ tid, categories }) {
  const [selCatId, setSelCatId] = useState("");
  const [finalizedMap, setFinalizedMap] = useState({});
  const unfinalizedCats = categories.filter((c) => !finalizedMap[`${c.event_category}__${c.age_category}`]);
  const lockedCats = categories.filter((c) => finalizedMap[`${c.event_category}__${c.age_category}`]);
  const selCat = useMemo(() => categories.find((c) => c.id === selCatId), [selCatId, categories]);
  const [state, setState] = useState({ seeds: [], pools: [], finalized: false });
  const [customPoolCount, setCustomPoolCount] = useState("");
  const [loading, setLoading] = useState(false);
  const finalized = !!state.finalized;

  useEffect(() => {
    (async () => {
      const nextMap = {};
      for (const c of categories) {
        try {
          const r = await api.get(`/organizer/tournaments/${tid}/pools`, { params: { event_category: c.event_category, age_category: c.age_category } });
          if (r.data.finalized) nextMap[`${c.event_category}__${c.age_category}`] = true;
        } catch {}
      }
      setFinalizedMap(nextMap);
    })();
    /* eslint-disable-next-line */
  }, [categories, tid]);

  const unlockCat = async (c) => {
    try {
      await api.post(`/organizer/tournaments/${tid}/pools/unlock`, null, { params: { event_category: c.event_category, age_category: c.age_category } });
      setFinalizedMap((m) => { const n = { ...m }; delete n[`${c.event_category}__${c.age_category}`]; return n; });
      setSelCatId(c.id);
      toast.success(`Unlocked ${catLabel(c)} — you can now edit`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = async () => {
    if (!selCat) return;
    try { const r = await api.get(`/organizer/tournaments/${tid}/pools`, { params: { event_category: selCat.event_category, age_category: selCat.age_category } }); setState({ seeds: r.data.seeds || [], pools: r.data.pools || [], finalized: !!r.data.finalized }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selCatId]);

  const params = () => ({ event_category: selCat.event_category, age_category: selCat.age_category });

  const importEntries = async () => {
    if (finalized) return;
    setLoading(true);
    try { const r = await api.post(`/organizer/tournaments/${tid}/pools/import-entries`, null, { params: params() }); toast.success(`${r.data.count} entries imported`); setState((s) => ({ ...s, seeds: r.data.seeds })); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const exportEntriesPdf = async () => {
    if (!selCat) return;
    try {
      const r = await api.get(`/organizer/tournaments/${tid}/pools/export.pdf`, { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `seeding-${selCat.event_category}-${selCat.age_category}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Seeding sheet downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
  };
  const addLateEntries = async () => {
    if (!selCat) return;
    if (!window.confirm("Add newly-approved late entries into the smallest pool and auto-create only their missing group matches?\nPlayed matches & knockouts stay untouched.")) return;
    try {
      const r = await api.post(`/organizer/tournaments/${tid}/pools/add-late-entries`, null, { params: params() });
      if (r.data.added === 0) { toast.info(r.data.message || "No late entries pending"); return; }
      toast.success(`${r.data.added} added · ${r.data.matches_created} new match${r.data.matches_created === 1 ? "" : "es"} created`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add late entries"); }
  };
  const autoSeed = async () => {
    if (finalized) return;
    const sorted = [...state.seeds].sort((a, b) => (b.seed_points || 0) - (a.seed_points || 0));
    try { await api.post(`/organizer/tournaments/${tid}/pools/seed`, { seeds: sorted }, { params: params() }); setState((s) => ({ ...s, seeds: sorted })); toast.success("Auto-seeded by ranking points"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteSeed = async (i) => {
    if (finalized) { toast.info("Pools finalized — unlock to edit"); return; }
    const s = state.seeds[i];
    if (!window.confirm(`Remove "${s.name || s.player_id}" from the seeding pool?\nThis only affects the seeding list — the player's registration stays intact.`)) return;
    const arr = state.seeds.filter((_, idx) => idx !== i);
    setState((st) => ({ ...st, seeds: arr }));
    try { await api.post(`/organizer/tournaments/${tid}/pools/seed`, { seeds: arr }, { params: params() }); toast.success("Seed removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const moveSeed = async (i, dir) => {
    if (finalized) return;
    const to = i + dir; if (to < 0 || to >= state.seeds.length) return;
    const arr = [...state.seeds]; [arr[i], arr[to]] = [arr[to], arr[i]];
    setState((s) => ({ ...s, seeds: arr }));
    try { await api.post(`/organizer/tournaments/${tid}/pools/seed`, { seeds: arr }, { params: params() }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const distribute = async () => {
    if (finalized) { toast.info("Pools finalized — unlock to redistribute"); return; }
    try {
      const pc = customPoolCount ? parseInt(customPoolCount, 10) : null;
      const r = await api.post(`/organizer/tournaments/${tid}/pools/distribute`, { pool_count: pc }, { params: params() });
      setState((s) => ({ ...s, pools: r.data.pools }));
      toast.success(`Distributed into ${r.data.pool_count} pools — hit Submit when you're happy with the layout`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const savePools = async (pools) => {
    try { await api.post(`/organizer/tournaments/${tid}/pools/save`, { pools }, { params: params() }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const movePlayer = (poolIdx, playerIdx, targetPoolIdx, targetPos) => {
    if (finalized) { toast.info("Pools finalized — unlock to edit"); return; }
    const pools = state.pools.map((p) => [...p]);
    const [player] = pools[poolIdx].splice(playerIdx, 1);
    const dest = pools[targetPoolIdx];
    const pos = Math.max(0, Math.min(targetPos ?? dest.length, dest.length));
    if (dest.length >= 8) { toast.error("Target pool full (max 8)"); return; }
    dest.splice(pos, 0, player);
    setState((s) => ({ ...s, pools }));
    savePools(pools);
  };
  const bumpInPool = (poolIdx, playerIdx, dir) => {
    if (finalized) return;
    const to = playerIdx + dir;
    if (to < 0 || to >= state.pools[poolIdx].length) return;
    const pools = state.pools.map((p) => [...p]);
    [pools[poolIdx][playerIdx], pools[poolIdx][to]] = [pools[poolIdx][to], pools[poolIdx][playerIdx]];
    setState((s) => ({ ...s, pools }));
    savePools(pools);
  };
  const finalize = async () => {
    if (!state.pools?.length) { toast.error("Distribute first"); return; }
    if (!window.confirm("Submit these pools?\nThey get locked and used to generate Draws & Fixtures.\nYou can Unlock later to edit.")) return;
    try {
      await api.post(`/organizer/tournaments/${tid}/pools/finalize`, null, { params: params() });
      setState((s) => ({ ...s, finalized: true }));
      setFinalizedMap((m) => ({ ...m, [`${selCat.event_category}__${selCat.age_category}`]: true }));
      toast.success("Pools submitted & locked");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const unlock = async () => {
    try {
      await api.post(`/organizer/tournaments/${tid}/pools/unlock`, null, { params: params() });
      setState((s) => ({ ...s, finalized: false }));
      setFinalizedMap((m) => { const n = { ...m }; delete n[`${selCat.event_category}__${selCat.age_category}`]; return n; });
      toast.success("Pools unlocked — you can now edit and re-submit");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="border border-jpa-border rounded-sm p-4 md:p-5 bg-white">
        <Label className="text-xs">Category</Label>
        <Select value={selCatId} onValueChange={setSelCatId}>
          <SelectTrigger data-testid="pools-cat-picker" className="mt-1 rounded-sm h-11 max-w-md"><SelectValue placeholder="Pick a category to manage" /></SelectTrigger>
          <SelectContent>{unfinalizedCats.length === 0 ? <SelectItem value="__none__" disabled>All categories are locked — unlock one below</SelectItem> : unfinalizedCats.map((c) => <SelectItem key={c.id} value={c.id}>{catLabel(c)} · {countLabel(c)}</SelectItem>)}</SelectContent>
        </Select>
        {!selCat && unfinalizedCats.length > 0 && <div className="mt-4 text-sm text-jpa-muted">Pick a category above to manage its seeding and pools.</div>}
      </div>

      {lockedCats.length > 0 && (
        <div className="border border-emerald-500 rounded-sm p-4 md:p-5 bg-emerald-50">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-emerald-700" />
            <h3 className="font-display text-lg tracking-wider text-emerald-800">Finalized pools ({lockedCats.length})</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {lockedCats.map((c) => (
              <div key={c.id} data-testid={`locked-cat-${c.id}`} className="flex items-center justify-between gap-2 bg-white border border-emerald-300 rounded-sm px-3 py-2">
                <div>
                  <div className="text-sm font-bold">{catLabel(c)}</div>
                  <div className="text-[10px] text-emerald-700 font-bold uppercase tracking-widest">Locked · {c.entry_count} entries</div>
                </div>
                <Button data-testid={`unlock-cat-${c.id}`} size="sm" variant="outline" onClick={() => unlockCat(c)} className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1 border-amber-500 text-amber-700 hover:bg-amber-50"><Unlock className="w-3 h-3" /> Unlock</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {selCat && (
        <>
          <div className="border border-jpa-border rounded-sm p-4 md:p-5 bg-white">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div>
                <h3 className="font-display text-2xl tracking-wider">Seeding · {catShort(selCat)}</h3>
                <p className="text-xs text-jpa-muted">{state.seeds.length} players in seeding pool{finalized ? " · locked" : ""}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button data-testid="pools-import" onClick={importEntries} disabled={loading || finalized} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><Download className="w-3 h-3" /> Import entries</Button>
                <Button data-testid="pools-auto-seed" onClick={autoSeed} disabled={state.seeds.length === 0 || finalized} className="rounded-sm bg-jpa-ink hover:bg-black text-white text-xs uppercase tracking-widest font-bold gap-1"><Shuffle className="w-3 h-3" /> Auto seed</Button>
              </div>
            </div>
            {state.seeds.length === 0 ? (
              <div className="border border-dashed border-jpa-border p-6 text-center text-sm text-jpa-muted rounded-sm">Click "Import entries" to load approved players from this category.</div>
            ) : (
              <ol className="divide-y divide-jpa-border">
                {state.seeds.map((s, i) => (
                  <li key={`${s.player_id || s.name}-${i}`} data-testid={`seed-${i}`} className="flex items-center justify-between gap-2 py-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-8 text-right font-mono text-xs font-bold text-jpa-primary">#{i + 1}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-bold truncate">{s.name || s.player_id}</div>
                        <div className="text-[11px] text-jpa-muted">{s.jpa_id || "—"}{s.partner_name ? ` · ↔ ${s.partner_name}` : ""} · {s.seed_points || 0} pts</div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button data-testid={`seed-up-${i}`} disabled={finalized} onClick={() => moveSeed(i, -1)} className="p-1.5 border border-jpa-border rounded-sm hover:border-jpa-primary disabled:opacity-40" aria-label="Up"><ChevronUp className="w-4 h-4" /></button>
                      <button data-testid={`seed-down-${i}`} disabled={finalized} onClick={() => moveSeed(i, 1)} className="p-1.5 border border-jpa-border rounded-sm hover:border-jpa-primary disabled:opacity-40" aria-label="Down"><ChevronDown className="w-4 h-4" /></button>
                      <button data-testid={`seed-delete-${i}`} disabled={finalized} onClick={() => deleteSeed(i)} className="p-1.5 border border-jpa-border rounded-sm hover:border-red-500 text-jpa-muted hover:text-red-600 disabled:opacity-40" aria-label="Remove seed"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {(state.seeds.length > 0 || state.pools.length > 0) && (
            <div className="border border-jpa-border rounded-sm p-4 md:p-5 bg-white">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div>
                  <h3 className="font-display text-2xl tracking-wider flex items-center gap-2">
                    Pools · {catShort(selCat)}
                    {finalized ? <span className="text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 px-2 py-1 rounded-sm inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span> : null}
                  </h3>
                  <p className="text-xs text-jpa-muted">Rules: min 3 players/pool (single pool of 3-5 allowed), max 8/pool. 2/4/8 pool layouts supported. Top seeds: 1→A, 2→last pool, 3→second-last, 4→second (others random).</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button data-testid="pools-export-pdf" onClick={exportEntriesPdf} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><Download className="w-3 h-3" /> Export entries PDF</Button>
                  <Input data-testid="pools-count" type="number" min={1} max={8} value={customPoolCount} onChange={(e) => setCustomPoolCount(e.target.value)} placeholder="auto" disabled={finalized || state.seeds.length < 3} className="rounded-sm w-24 h-10" />
                  <Button data-testid="pools-distribute" onClick={distribute} disabled={finalized || state.seeds.length < 3} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold">Distribute</Button>
                  {finalized && (
                    <Button data-testid="pools-add-late" onClick={addLateEntries} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1 border-jpa-primary text-jpa-primary hover:bg-jpa-primary/5"><Plus className="w-3 h-3" /> Add late entries</Button>
                  )}
                  {!finalized && state.pools?.length > 0 && (
                    <Button data-testid="pools-finalize" onClick={finalize} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs uppercase tracking-widest font-bold gap-1"><Lock className="w-3 h-3" /> Submit pools</Button>
                  )}
                  {finalized && (
                    <Button data-testid="pools-unlock" onClick={unlock} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1 border-amber-500 text-amber-700 hover:bg-amber-50"><Unlock className="w-3 h-3" /> Unlock to edit</Button>
                  )}
                </div>
              </div>
              {state.seeds.length > 0 && state.seeds.length < 3 && state.pools.length === 0 && (
                <div className="mb-3 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2">Only {state.seeds.length} seed{state.seeds.length > 1 ? "s" : ""} imported — need at least 3 to build a pool.</div>
              )}

              {state.pools.length === 0 ? (
                <div className="border border-dashed border-jpa-border p-6 text-center text-sm text-jpa-muted rounded-sm">Hit "Distribute" to build pools automatically.</div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {state.pools.map((pool, pi) => (
                    <div key={pi} data-testid={`pool-${pi}`} className={`border rounded-sm overflow-hidden ${finalized ? "border-emerald-500" : "border-jpa-border"}`}>
                      <div className="bg-jpa-primary text-white px-3 py-2 flex items-center justify-between">
                        <div className="text-xs font-bold uppercase tracking-widest">Pool {String.fromCharCode(65 + pi)}</div>
                        <div className="text-[10px] font-bold">{pool.length}/8</div>
                      </div>
                      <ul className="divide-y divide-jpa-border">
                        {pool.map((p, pxi) => (
                          <li key={`${p.player_id || p.name}-${pxi}`} data-testid={`pool-${pi}-player-${pxi}`} className="flex items-center justify-between gap-2 px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="w-6 text-right font-mono text-[11px] font-bold text-jpa-primary">{pxi + 1}</span>
                              <div className="min-w-0">
                                <div className="text-sm font-bold truncate">{p.name}</div>
                                <div className="text-[10px] text-jpa-muted">{p.jpa_id || "—"} · {p.seed_points || 0} pts</div>
                              </div>
                            </div>
                            {!finalized && (
                              <div className="flex items-center gap-1">
                                <button onClick={() => bumpInPool(pi, pxi, -1)} className="p-1 border border-jpa-border rounded-sm hover:border-jpa-primary" aria-label="Up"><ChevronUp className="w-3 h-3" /></button>
                                <button onClick={() => bumpInPool(pi, pxi, 1)} className="p-1 border border-jpa-border rounded-sm hover:border-jpa-primary" aria-label="Down"><ChevronDown className="w-3 h-3" /></button>
                                <Select value="" onValueChange={(v) => movePlayer(pi, pxi, parseInt(v, 10))}>
                                  <SelectTrigger data-testid={`move-${pi}-${pxi}`} className="rounded-sm h-7 w-20 text-[10px]"><SelectValue placeholder="Move" /></SelectTrigger>
                                  <SelectContent>
                                    {state.pools.map((_, ti) => ti !== pi && <SelectItem key={ti} value={String(ti)}>→ Pool {String.fromCharCode(65 + ti)}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =========================================================================
// TAB 0 — Player Check-In (registration desk)
// =========================================================================
function CheckInTab({ tid }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | pending | done
  const [editing, setEditing] = useState(null);
  const load = async () => {
    try { const r = await api.get(`/organizer/tournaments/${tid}/check-in`); setRows(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tid]);
  const toggle = async (row, val) => {
    try { await api.post(`/organizer/tournaments/${tid}/check-in/toggle`, { key: row.key, checked_in: val }); toast.success(val ? `${row.name} checked in` : `${row.name} checked out`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    if (!editing) return;
    const patch = { key: editing.key };
    if (editing.name !== undefined) patch.name = editing.name;
    if (editing.aadhaar_number !== undefined) patch.aadhaar_number = (editing.aadhaar_number || "").replace(/\D/g, "") || null;
    if (editing.tshirt_size !== undefined) patch.tshirt_size = editing.tshirt_size;
    try { await api.patch(`/organizer/tournaments/${tid}/check-in/edit`, patch); toast.success("Updated"); setEditing(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const visible = rows.filter((r) => {
    if (filter === "pending" && r.checked_in) return false;
    if (filter === "done" && !r.checked_in) return false;
    if (q && !((r.name || "").toLowerCase().includes(q.toLowerCase()) || (r.aadhaar_number || "").includes(q))) return false;
    return true;
  });
  const done = rows.filter((r) => r.checked_in).length;
  return (
    <div className="space-y-4" data-testid="checkin-panel">
      <div className="border border-jpa-border rounded-sm p-4 md:p-5 bg-white">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="font-display text-2xl tracking-wider flex items-center gap-2"><UserCheck className="w-5 h-5 text-jpa-primary" /> Player Check-In</h3>
            <p className="text-xs text-jpa-muted">{done}/{rows.length} checked in. Referees can't start a match unless both players are checked in.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Input data-testid="checkin-search" placeholder="Search name or Aadhaar" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm h-10 w-56" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="checkin-filter" className="rounded-sm h-10 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({rows.length})</SelectItem>
                <SelectItem value="pending">Pending ({rows.length - done})</SelectItem>
                <SelectItem value="done">Checked in ({done})</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="checkin-reload" variant="outline" onClick={load} className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><RefreshCw className="w-3 h-3" /> Reload</Button>
          </div>
        </div>
        {visible.length === 0 ? (
          <div className="border border-dashed border-jpa-border p-6 text-center text-sm text-jpa-muted rounded-sm">No players match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest font-bold text-jpa-muted border-b border-jpa-border">
                <tr><th className="text-left py-2 px-2">#</th><th className="text-left px-2">Name</th><th className="text-left px-2">Aadhaar</th><th className="text-left px-2">T-shirt</th><th className="text-left px-2">Events</th><th className="text-right px-2">Action</th></tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr key={r.key} data-testid={`checkin-row-${r.key}`} className={`border-b border-jpa-border/40 ${r.checked_in ? "bg-emerald-50/60" : ""}`}>
                    <td className="py-2 px-2 text-xs text-jpa-muted">{i + 1}</td>
                    <td className="px-2">
                      <div className="font-bold">{r.name}</div>
                      <div className="text-[10px] text-jpa-muted">{r.jpa_id || "no JPA ID"}{r.whatsapp ? ` · ${r.whatsapp}` : ""}</div>
                    </td>
                    <td className="px-2 font-mono text-xs">{r.aadhaar_number || "—"}</td>
                    <td className="px-2 text-xs">{r.tshirt_size || "—"}</td>
                    <td className="px-2">
                      <div className="flex flex-wrap gap-1">
                        {r.events.map((e, ei) => <span key={ei} className="text-[10px] font-bold uppercase tracking-widest bg-jpa-primary/10 text-jpa-primary px-1.5 py-0.5 rounded-sm">{SHORT[e.event_category] || e.event_category} {ageLbl(e.age_category)}</span>)}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-right">
                      <div className="flex gap-1 justify-end">
                        {r.checked_in ? (
                          <Button data-testid={`checkin-undo-${r.key}`} size="sm" variant="outline" onClick={() => toggle(r, false)} className="rounded-sm text-[10px] uppercase tracking-widest font-bold gap-1 h-8 border-amber-500 text-amber-700"><X className="w-3 h-3" /> Undo</Button>
                        ) : (
                          <Button data-testid={`checkin-toggle-${r.key}`} size="sm" onClick={() => toggle(r, true)} className="rounded-sm text-[10px] uppercase tracking-widest font-bold bg-emerald-600 hover:bg-emerald-700 text-white gap-1 h-8"><Check className="w-3 h-3" /> Check In</Button>
                        )}
                        <Button data-testid={`checkin-edit-${r.key}`} size="sm" variant="outline" onClick={() => setEditing({ ...r })} className="rounded-sm text-[10px] uppercase tracking-widest font-bold h-8 px-2"><Edit3 className="w-3 h-3" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <div className="bg-white rounded-sm max-w-lg w-full p-5 border border-jpa-border">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display text-xl tracking-wider">Edit player</h4>
              <button onClick={() => setEditing(null)} className="p-1"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid gap-3">
              <div><Label className="text-xs">Name</Label><Input data-testid="checkin-edit-name" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Aadhaar (12 digits)</Label><Input data-testid="checkin-edit-aadhaar" value={editing.aadhaar_number || ""} maxLength={12} onChange={(e) => setEditing({ ...editing, aadhaar_number: e.target.value.replace(/\D/g, "") })} className="mt-1 rounded-sm font-mono" /></div>
              <div><Label className="text-xs">T-shirt size</Label>
                <Select value={editing.tshirt_size || ""} onValueChange={(v) => setEditing({ ...editing, tshirt_size: v })}>
                  <SelectTrigger data-testid="checkin-edit-tshirt" className="mt-1 rounded-sm h-10"><SelectValue placeholder="Pick size" /></SelectTrigger>
                  <SelectContent>{["XS", "Small", "Medium", "Large", "XL", "XXL"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm text-xs uppercase tracking-widest font-bold">Cancel</Button>
              <Button data-testid="checkin-save" onClick={saveEdit} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// =========================================================================
// TAB 3 — Draws & Fixtures (per-round scoring rules inline)
// =========================================================================
const ROUND_LABELS = { group: "Group (Pools)", r32: "Round of 32", r16: "Round of 16", qf: "Quarter Final", sf: "Semi Final", "3rd_place": "3rd Place", final: "Final" };
const ROUND_KEYS = ["group", "r32", "r16", "qf", "sf", "3rd_place", "final"];
const DEFAULT_ROUND_SCORING = {
  group: { points_per_game: 11, win_by: 2, best_of: 1, game_type: "rally" },
  r32: { points_per_game: 11, win_by: 2, best_of: 3, game_type: "rally" },
  r16: { points_per_game: 11, win_by: 2, best_of: 3, game_type: "rally" },
  qf: { points_per_game: 11, win_by: 2, best_of: 3, game_type: "rally" },
  sf: { points_per_game: 15, win_by: 2, best_of: 3, game_type: "rally" },
  "3rd_place": { points_per_game: 15, win_by: 2, best_of: 3, game_type: "rally" },
  final: { points_per_game: 15, win_by: 2, best_of: 3, game_type: "rally" },
};

function DrawsTab({ tid, categories, onCategoriesChange }) {
  const [selCatId, setSelCatId] = useState("");
  const selCat = useMemo(() => categories.find((c) => c.id === selCatId), [selCatId, categories]);
  const [cfg, setCfg] = useState({ format: "group_ko", advance_per_group: 2, round_scoring: JSON.parse(JSON.stringify(DEFAULT_ROUND_SCORING)) });
  const [matches, setMatches] = useState([]);
  const [editingMatch, setEditingMatch] = useState(null);
  const fixturesLocked = !!selCat?.fixtures_locked;
  const [showAll, setShowAll] = useState(false);
  const loadMatches = async () => {
    if (showAll) {
      try { const r = await api.get(`/organizer/tournaments/${tid}/matches`); setMatches(r.data); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      return;
    }
    if (!selCat) return setMatches([]);
    try { const r = await api.get(`/organizer/tournaments/${tid}/matches`, { params: { event_category: selCat.event_category, age_category: selCat.age_category } }); setMatches(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // Load per-category scoring + format whenever a different category is picked
  useEffect(() => {
    loadMatches();
    if (!selCat) return;
    (async () => {
      try {
        const r = await api.get(`/organizer/tournaments/${tid}/categories/${selCat.id}/scoring`);
        const rs = (r.data.round_scoring && Object.keys(r.data.round_scoring).length > 0)
          ? { ...JSON.parse(JSON.stringify(DEFAULT_ROUND_SCORING)), ...r.data.round_scoring }
          : JSON.parse(JSON.stringify(DEFAULT_ROUND_SCORING));
        setCfg((c) => ({ ...c, round_scoring: rs, format: r.data.fixtures_format || c.format }));
      } catch { /* silent — new category */ }
    })();
    /* eslint-disable-next-line */
  }, [selCatId, showAll]);
  const setRound = (rk, key, value) => setCfg((c) => ({ ...c, round_scoring: { ...c.round_scoring, [rk]: { ...c.round_scoring[rk], [key]: value } } }));
  const saveScoring = async () => {
    if (!selCat) return;
    try {
      await api.put(`/organizer/tournaments/${tid}/categories/${selCat.id}/scoring`, { round_scoring: cfg.round_scoring });
      toast.success(`Saved scoring rules for ${catLabel(selCat)}`);
      onCategoriesChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const generate = async () => {
    if (!selCat) return;
    if (fixturesLocked) { toast.info("Fixtures locked — click Unlock fixtures first"); return; }
    if (matches.length && !window.confirm(`This will wipe the ${matches.length} existing match(es) for ${catLabel(selCat)} and regenerate.\nAny scores already entered will be lost.`)) return;
    try {
      const r = await api.post(`/organizer/tournaments/${tid}/draws/generate`, { ...cfg, event_category: selCat.event_category, age_category: selCat.age_category });
      toast.success(`${r.data.matches_created} match${r.data.matches_created === 1 ? "" : "es"} generated · fixtures locked automatically`);
      loadMatches();
      onCategoriesChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const unlockFixtures = async () => {
    if (!selCat) return;
    if (!window.confirm(`Unlock fixtures for ${catLabel(selCat)}? You can then Regenerate. Existing matches stay until you click Regenerate.`)) return;
    try {
      await api.post(`/organizer/tournaments/${tid}/fixtures/unlock`, null, { params: { event_category: selCat.event_category, age_category: selCat.age_category } });
      toast.success("Fixtures unlocked");
      onCategoriesChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addThirdPlace = async () => {
    if (!selCat) return;
    if (!window.confirm(`Add a 3rd place playoff match for ${catLabel(selCat)}?\nSemi-final losers will be placed into it automatically.`)) return;
    try {
      await api.post(`/organizer/tournaments/${tid}/matches/add-third-place`, null, { params: { event_category: selCat.event_category, age_category: selCat.age_category } });
      toast.success("3rd place match added");
      loadMatches();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const printFixtures = async () => {
    if (!selCat) return;
    try {
      const r = await api.get(`/organizer/tournaments/${tid}/fixtures/export.pdf`, { params: { event_category: selCat.event_category, age_category: selCat.age_category }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `fixtures-${selCat.event_category}-${selCat.age_category}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Fixture sheet downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Print failed"); }
  };
  const [referees, setReferees] = useState([]);
  const [assignMode, setAssignMode] = useState("per_category");
  const [playerOptions, setPlayerOptions] = useState([]);
  useEffect(() => {
    if (!editingMatch) { setPlayerOptions([]); return; }
    api.get(`/organizer/tournaments/${tid}/matches/player-options`, { params: { event_category: editingMatch.event_category, age_category: editingMatch.age_category } })
      .then((r) => setPlayerOptions(r.data)).catch(() => setPlayerOptions([]));
    /* eslint-disable-next-line */
  }, [editingMatch?.id]);
  useEffect(() => { api.get(`/organizer/tournaments/${tid}/referees`).then((r) => setReferees(r.data)).catch(() => {}); }, [tid]);
  const unlockScore = async (m) => {
    if (!window.confirm(`Unlock the submitted score for ${m.player_a_name} vs ${m.player_b_name}?\nThe match reopens as IN PROGRESS so the referee can re-enter.`)) return;
    try {
      await api.post(`/organizer/tournaments/${tid}/matches/${m.id}/unlock-score`);
      toast.success("Score unlocked — match is open for re-entry");
      setEditingMatch(null);
      loadMatches();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const autoAssignRefs = async () => {
    try {
      const r = await api.post(`/organizer/tournaments/${tid}/matches/auto-assign-referees`, null, { params: { mode: assignMode } });
      toast.success(`Assigned ${r.data.assigned} match${r.data.assigned === 1 ? "" : "es"} across ${r.data.referees} referee(s) — ${assignMode === "per_category" ? "one referee per category" : "round-robin"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveMatch = async () => {
    if (!editingMatch) return;
    const patch = { court: editingMatch.court || null, scheduled_day: editingMatch.scheduled_day || null, scheduled_slot: editingMatch.scheduled_slot || null, referee_id: editingMatch.referee_id || null };
    const orig = matches.find((x) => x.id === editingMatch.id);
    if (orig && editingMatch.player_a_entry_id !== orig.player_a_entry_id) patch.player_a_entry_id = editingMatch.player_a_entry_id;
    if (orig && editingMatch.player_b_entry_id !== orig.player_b_entry_id) patch.player_b_entry_id = editingMatch.player_b_entry_id;
    try { await api.patch(`/organizer/tournaments/${tid}/matches/${editingMatch.id}`, patch); toast.success("Match updated"); setEditingMatch(null); loadMatches(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const applyToAll = async () => {
    if (!matches.length) return;
    if (!window.confirm("Apply the current per-round scoring rules to ALL matches in this tournament?")) return;
    try {
      const other = await api.get(`/organizer/tournaments/${tid}/matches`);
      let n = 0;
      for (const m of other.data) {
        const sc = cfg.round_scoring[m.round] || cfg.round_scoring.group;
        await api.patch(`/organizer/tournaments/${tid}/matches/${m.id}`, sc);
        n += 1;
      }
      toast.success(`Applied per-round scoring rules to ${n} matches`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const grouped = matches.reduce((acc, m) => {
    const catPrefix = showAll ? `${EVT_LABELS[m.event_category] || m.event_category} · ${ageLbl(m.age_category)} — ` : "";
    const k = catPrefix + (m.group_label || (ROUND_LABELS[m.round] || m.round));
    (acc[k] = acc[k] || []).push(m);
    return acc;
  }, {});
  return (
    <div className="space-y-4">
      <div className="border border-jpa-border rounded-sm p-4 md:p-5 bg-white">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-5 h-5 text-jpa-primary" />
          <h3 className="font-display text-2xl tracking-wider">Draws & Fixtures</h3>
        </div>
        <Label className="text-xs">Pick category</Label>
        <Select value={selCatId} onValueChange={setSelCatId}>
          <SelectTrigger data-testid="draws-cat" className="mt-1 rounded-sm h-11 max-w-md"><SelectValue placeholder="Pick a category" /></SelectTrigger>
          <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{catLabel(c)} · {countLabel(c)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {selCat && (
        <div className="border border-jpa-border rounded-sm p-4 md:p-5 bg-white space-y-4">
          <h4 className="font-display text-lg tracking-wider">Wizard · {catLabel(selCat)}</h4>
          <p className="text-xs text-jpa-muted">Groups are read directly from your finalized pools (Players &amp; Pools tab). No need to set group size here.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Draw format</Label>
              <Select value={cfg.format} onValueChange={(v) => setCfg({ ...cfg, format: v })}>
                <SelectTrigger data-testid="draws-format" className="mt-1 rounded-sm h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="group_ko">Group + Knockout (uses finalized pools)</SelectItem>
                  <SelectItem value="round_robin">Round Robin within pools</SelectItem>
                  <SelectItem value="single_elim">Single Elimination (no pools needed)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cfg.format === "group_ko" && (
              <div><Label className="text-xs">Advance per pool</Label>
                <Select value={String(cfg.advance_per_group)} onValueChange={(v) => setCfg({ ...cfg, advance_per_group: parseInt(v) })}>
                  <SelectTrigger data-testid="draws-advance" className="mt-1 rounded-sm h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Top 1 per pool</SelectItem>
                    <SelectItem value="2">Top 2 per pool</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-jpa-border">
            <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary mb-2">Scoring rules per round</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest font-bold text-jpa-muted border-b border-jpa-border">
                    <th className="text-left py-1.5 pr-2">Round</th>
                    <th className="text-left py-1.5 pr-2">Points</th>
                    <th className="text-left py-1.5 pr-2">Win by</th>
                    <th className="text-left py-1.5 pr-2">Best of</th>
                    <th className="text-left py-1.5">Game type</th>
                  </tr>
                </thead>
                <tbody>
                  {ROUND_KEYS.map((rk) => (
                    <tr key={rk} data-testid={`scoring-row-${rk}`} className="border-b border-jpa-border/40">
                      <td className="py-1.5 pr-2 font-bold whitespace-nowrap">{ROUND_LABELS[rk]}</td>
                      <td className="py-1.5 pr-2">
                        <select data-testid={`scoring-${rk}-points`} value={cfg.round_scoring[rk].points_per_game} onChange={(e) => setRound(rk, "points_per_game", parseInt(e.target.value))} className="border border-jpa-border rounded-sm h-8 px-2 text-xs bg-white">
                          {[11, 15, 21].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </td>
                      <td className="py-1.5 pr-2">
                        <select data-testid={`scoring-${rk}-winby`} value={cfg.round_scoring[rk].win_by} onChange={(e) => setRound(rk, "win_by", parseInt(e.target.value))} className="border border-jpa-border rounded-sm h-8 px-2 text-xs bg-white">
                          <option value={1}>1</option><option value={2}>2</option>
                        </select>
                      </td>
                      <td className="py-1.5 pr-2">
                        <select data-testid={`scoring-${rk}-bestof`} value={cfg.round_scoring[rk].best_of} onChange={(e) => setRound(rk, "best_of", parseInt(e.target.value))} className="border border-jpa-border rounded-sm h-8 px-2 text-xs bg-white">
                          {[1, 3, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </td>
                      <td className="py-1.5">
                        <select data-testid={`scoring-${rk}-gametype`} value={cfg.round_scoring[rk].game_type} onChange={(e) => setRound(rk, "game_type", e.target.value)} className="border border-jpa-border rounded-sm h-8 px-2 text-xs bg-white">
                          <option value="rally">Rally</option><option value="side_out">Side out</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap pt-2 items-center">
            {fixturesLocked && (
              <span data-testid="fixtures-locked-badge" className="text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 px-2.5 py-1.5 rounded-sm inline-flex items-center gap-1 border border-emerald-300"><Lock className="w-3 h-3" /> Fixtures locked</span>
            )}
            <Button data-testid="draws-generate" onClick={generate} disabled={fixturesLocked} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold disabled:opacity-40 disabled:cursor-not-allowed">{matches.length ? "Regenerate fixtures" : "Generate fixtures"}</Button>
            {fixturesLocked && (
              <Button data-testid="draws-unlock" onClick={unlockFixtures} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1 border-amber-500 text-amber-700 hover:bg-amber-50"><Unlock className="w-3 h-3" /> Unlock fixtures</Button>
            )}
            <Button data-testid="draws-save-scoring" onClick={saveScoring} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold">Save scoring for this category</Button>
            {matches.length > 0 && <Button data-testid="draws-print" onClick={printFixtures} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><Download className="w-3 h-3" /> Print fixtures PDF</Button>}
            {selCat && !showAll && matches.length > 0 && !matches.some((m) => m.round === "3rd_place") && (
              <Button data-testid="draws-add-third-place" onClick={addThirdPlace} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1 border-jpa-primary text-jpa-primary hover:bg-jpa-primary/5"><Trophy className="w-3 h-3" /> Add 3rd place match</Button>
            )}
            <Button data-testid="draws-auto-refs" onClick={autoAssignRefs} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><Shield className="w-3 h-3" /> Auto assign referees</Button>
            <Select value={assignMode} onValueChange={setAssignMode}>
              <SelectTrigger data-testid="draws-assign-mode" className="rounded-sm h-9 w-[210px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="per_category">Fair: 1 referee per category</SelectItem>
                <SelectItem value="round_robin">Spread: round-robin all matches</SelectItem>
              </SelectContent>
            </Select>
            {matches.length > 0 && <Button data-testid="draws-apply-all" onClick={applyToAll} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold">Apply scoring to ALL categories</Button>}
          </div>
        </div>
      )}

      {!selCat && !showAll ? (
        <div data-testid="draws-fixtures-empty" className="border border-dashed border-jpa-border rounded-sm p-8 text-center bg-white">
          <div className="text-sm text-jpa-muted mb-3">Pick a category above to see its fixtures — nothing is shown by default.</div>
          <Button data-testid="draws-show-all" onClick={() => setShowAll(true)} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1"><ListOrdered className="w-3.5 h-3.5" /> Show all fixtures</Button>
        </div>
      ) : matches.length > 0 ? (
        <div className="border border-jpa-border rounded-sm p-4 md:p-5 bg-white">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h4 className="font-display text-lg tracking-wider">{showAll ? "All fixtures" : `Fixtures · ${catLabel(selCat)}`} · {matches.length} match{matches.length === 1 ? "" : "es"}</h4>
            <Button data-testid="draws-toggle-all" onClick={() => setShowAll((v) => !v)} variant="outline" size="sm" className="rounded-sm text-[10px] uppercase tracking-widest font-bold gap-1"><ListOrdered className="w-3 h-3" /> {showAll ? "Show picked category only" : "All fixtures"}</Button>
          </div>
          <div className="space-y-4">
            {Object.entries(grouped).map(([label, list]) => (
              <div key={label}>
                <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary mb-2">{label} · {list.length} match{list.length === 1 ? "" : "es"}</div>
                <div className="space-y-1">
                  {list.map((m, i) => (
                    <div key={m.id} data-testid={`match-row-${m.id}`} className="flex items-center justify-between gap-2 border border-jpa-border rounded-sm px-3 py-2 text-sm hover:border-jpa-primary">
                      <div className="min-w-0">
                        <div className="text-xs font-mono text-jpa-muted">M{i + 1} · {m.status.toUpperCase()}{m.court ? ` · Court ${m.court}` : ""}{m.referee_id ? " · Ref ✓" : ""}</div>
                        <div className="font-bold truncate">{m.player_a_name || "TBD"} <span className="text-jpa-muted">vs</span> {m.player_b_name || "TBD"}</div>
                        <div className="text-[10px] text-jpa-muted">{m.points_per_game}pt · win-by-{m.win_by} · best of {m.best_of} · {m.game_type === "side_out" ? "side-out" : "rally"}</div>
                      </div>
                      <Button data-testid={`match-edit-${m.id}`} size="sm" variant="outline" onClick={() => setEditingMatch({ ...m })} className="rounded-sm text-[10px] uppercase tracking-widest font-bold h-8 px-2"><Edit3 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div data-testid="draws-fixtures-none" className="border border-dashed border-jpa-border rounded-sm p-8 text-center text-sm text-jpa-muted bg-white">No fixtures yet for this category — generate them above.</div>
      )}

      {editingMatch && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setEditingMatch(null)}>
          <div className="bg-white rounded-sm max-w-lg w-full p-5 border border-jpa-border">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display text-xl tracking-wider">Edit match</h4>
              <button onClick={() => setEditingMatch(null)} className="p-1"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-sm mb-3"><b>{editingMatch.player_a_name || "TBD"}</b> vs <b>{editingMatch.player_b_name || "TBD"}</b></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Court</Label><Input data-testid="match-edit-court" type="number" min={1} value={editingMatch.court || ""} onChange={(e) => setEditingMatch({ ...editingMatch, court: parseInt(e.target.value) || null })} className="mt-1 rounded-sm h-10" /></div>
              <div><Label className="text-xs">Day #</Label><Input data-testid="match-edit-day" type="number" min={1} value={editingMatch.scheduled_day || ""} onChange={(e) => setEditingMatch({ ...editingMatch, scheduled_day: parseInt(e.target.value) || null })} className="mt-1 rounded-sm h-10" /></div>
              <div><Label className="text-xs">Slot #</Label><Input data-testid="match-edit-slot" type="number" min={0} value={editingMatch.scheduled_slot || ""} onChange={(e) => setEditingMatch({ ...editingMatch, scheduled_slot: parseInt(e.target.value) || null })} className="mt-1 rounded-sm h-10" /></div>
            </div>
            <div className="mt-3">
              <Label className="text-xs">Assigned referee</Label>
              <Select value={editingMatch.referee_id || "__none__"} onValueChange={(v) => setEditingMatch({ ...editingMatch, referee_id: v === "__none__" ? null : v })}>
                <SelectTrigger data-testid="match-edit-referee" className="mt-1 rounded-sm h-10"><SelectValue placeholder="No referee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No referee —</SelectItem>
                  {referees.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} (@{r.username})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(editingMatch.status === "scheduled" || editingMatch.status === "in_progress") && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {["a", "b"].map((s) => (
                  <div key={s}>
                    <Label className="text-xs">Side {s.toUpperCase()} player</Label>
                    <Select
                      value={editingMatch[`player_${s}_entry_id`] || "__keep__"}
                      onValueChange={(v) => {
                        if (v === "__keep__") return;
                        const o = playerOptions.find((x) => x.id === v);
                        setEditingMatch({ ...editingMatch, [`player_${s}_entry_id`]: v, [`player_${s}_name`]: o?.player_name });
                      }}
                    >
                      <SelectTrigger data-testid={`match-edit-player-${s}`} className="mt-1 rounded-sm h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__keep__">Keep: {editingMatch[`player_${s}_name`] || "TBD"}</SelectItem>
                        {playerOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.player_name}{o.seed ? ` · Seed ${o.seed}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between gap-2 mt-4">
              <div>
                {editingMatch.status === "completed" && (
                  <Button data-testid="match-unlock-score" onClick={() => unlockScore(editingMatch)} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold border-amber-500 text-amber-700 hover:bg-amber-50 gap-1"><Unlock className="w-3 h-3" /> Unlock score</Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditingMatch(null)} className="rounded-sm text-xs uppercase tracking-widest font-bold">Cancel</Button>
                <Button data-testid="match-save" onClick={saveMatch} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold">Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// =========================================================================
// Root page — new 6-tab layout (Tabs 3-6 coming soon)
// =========================================================================
export default function OrganizerHubPage() {
  const { id: tid } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [tab, setTab] = useState("checkin");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get(`/tournaments/${tid}`).then((r) => setT(r.data)).catch(() => { toast.error("Tournament not found"); nav("/tournaments"); });
  }, [tid, nav]);

  if (!t) return <div className="max-w-6xl mx-auto p-8 text-jpa-muted">Loading…</div>;

  return (
    <div data-testid="organizer-hub" className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">Organizer Hub</div>
        <h1 className="font-display text-3xl md:text-5xl tracking-wider leading-tight">{t.name}</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-1 h-auto p-1 bg-jpa-surface rounded-sm">
          <TabsTrigger value="checkin" data-testid="tab-checkin" className="text-[11px] md:text-xs uppercase tracking-widest font-bold rounded-sm px-2 py-2.5">Check-In</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories" className="text-[11px] md:text-xs uppercase tracking-widest font-bold rounded-sm px-2 py-2.5">Categories</TabsTrigger>
          <TabsTrigger value="pools" data-testid="tab-pools" className="text-[11px] md:text-xs uppercase tracking-widest font-bold rounded-sm px-2 py-2.5">Pools</TabsTrigger>
          <TabsTrigger value="draws" data-testid="tab-draws" className="text-[11px] md:text-xs uppercase tracking-widest font-bold rounded-sm px-2 py-2.5">Fixtures</TabsTrigger>
          <TabsTrigger value="courts" data-testid="tab-courts" className="text-[11px] md:text-xs uppercase tracking-widest font-bold rounded-sm px-2 py-2.5">Courts</TabsTrigger>
          <TabsTrigger value="standings" data-testid="tab-standings" className="text-[11px] md:text-xs uppercase tracking-widest font-bold rounded-sm px-2 py-2.5">Standings</TabsTrigger>
        </TabsList>

        <TabsContent value="checkin" className="mt-6">
          <CheckInTab tid={tid} />
        </TabsContent>
        <TabsContent value="categories" className="mt-6">
          <CategoriesTab tid={tid} onCategoriesChange={setCategories} />
        </TabsContent>
        <TabsContent value="pools" className="mt-6">
          <PoolsTab tid={tid} categories={categories} />
        </TabsContent>
        <TabsContent value="draws" className="mt-6">
          <DrawsTab tid={tid} categories={categories} onCategoriesChange={() => api.get(`/organizer/tournaments/${tid}/categories`).then((r) => setCategories(r.data)).catch(() => {})} />
        </TabsContent>
        <TabsContent value="courts" className="mt-6">
          <div className="border border-dashed border-jpa-border p-10 text-center text-sm text-jpa-muted rounded-sm">Courts & Schedule — coming next.</div>
        </TabsContent>
        <TabsContent value="standings" className="mt-6">
          <div className="border border-dashed border-jpa-border p-10 text-center text-sm text-jpa-muted rounded-sm">Standings & Results — coming next.</div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
